import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useUser } from '../../components/UserContext.tsx';
import { hasPersistedWrapKey, useCrypto } from '../../components/CryptoContext.tsx';
import { hasConfiguredSssMetadata } from './settingsEncryptionStatus.ts';
import {
  fetchPushConfig,
  getNotificationPermission,
  getPushSupport,
  isBrowserPushSubscribed,
  isIosBrowser,
  isStandaloneDisplayMode,
  subscribeToPush,
  unsubscribeFromPush,
  type PushConfig
} from '../../utils/pushNotifications.ts';

function NotificationsSection({ loggedIn }: { loggedIn: boolean }) {
  const pushSupport = useMemo(() => getPushSupport(), []);
  const [pushConfig, setPushConfig] = useState<PushConfig | null>(null);
  const [permission, setPermission] = useState(getNotificationPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const refreshPushState = useCallback(async () => {
    setPermission(getNotificationPermission());
    if (!loggedIn || !pushSupport.supported) {
      setPushConfig(null);
      setSubscribed(false);
      return;
    }
    try {
      const config = await fetchPushConfig();
      setPushConfig(config);
      setConfigError(null);
      if (config.enabled) {
        setSubscribed(await isBrowserPushSubscribed());
      } else {
        setSubscribed(false);
      }
    } catch (_) {
      setPushConfig(null);
      setConfigError('Could not load push notification settings from the server.');
      setSubscribed(false);
    }
  }, [loggedIn, pushSupport.supported]);

  useEffect(() => {
    refreshPushState();
  }, [refreshPushState]);

  const handleEnablePush = async () => {
    setPushError(null);
    setPushMessage(null);
    setPushBusy(true);
    try {
      const config = pushConfig || await fetchPushConfig();
      if (!config.enabled || !config.publicKey) {
        throw new Error('Push notifications are not configured on this server.');
      }
      await subscribeToPush(config.publicKey);
      setPermission(getNotificationPermission());
      setSubscribed(true);
      setPushMessage('Notifications are enabled for this browser.');
    } catch (err) {
      setPushError(err instanceof Error ? err.message : 'Could not enable notifications');
      await refreshPushState();
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushError(null);
    setPushMessage(null);
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      setPushMessage('Notifications are disabled for this browser.');
    } catch (err) {
      setPushError(err instanceof Error ? err.message : 'Could not disable notifications');
      await refreshPushState();
    } finally {
      setPushBusy(false);
    }
  };

  let statusText = 'Checking…';
  if (!pushSupport.supported) {
    statusText = pushSupport.reason || 'Not supported in this browser';
  } else if (!loggedIn) {
    statusText = 'Log in to manage notifications';
  } else if (configError) {
    statusText = configError;
  } else if (pushConfig && !pushConfig.enabled) {
    statusText = 'Not configured on this server';
  } else if (permission === 'denied') {
    statusText = 'Blocked in browser settings';
  } else if (subscribed) {
    statusText = 'Enabled on this browser';
  } else if (permission === 'default') {
    statusText = 'Not enabled yet';
  } else {
    statusText = 'Not subscribed';
  }

  const canEnable = loggedIn
    && pushSupport.supported
    && !configError
    && pushConfig?.enabled
    && permission !== 'denied'
    && !subscribed;
  const canDisable = loggedIn && pushSupport.supported && subscribed;

  return (
    <section className="mt-5 pt-4 border-top">
      <h2 className="h4 mb-3">Notifications</h2>
      <p>
        Optional browser alerts when someone sends a new message or photo.
        Notification text stays generic — it never includes message content.
      </p>
      {isIosBrowser() && !isStandaloneDisplayMode() && (
        <Alert variant="warning">
          On iPhone and iPad, Web Push works after you add this site to the Home Screen
          (Share → Add to Home Screen), then open it from the icon and enable notifications here.
        </Alert>
      )}
      <p className="mb-2"><strong>Status:</strong> {statusText}</p>
      {permission === 'denied' && (
        <p className="text-muted">
          Unblock notifications for this site in your browser settings, then reload this page.
        </p>
      )}
      <div className="d-flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={!canEnable || pushBusy}
          onClick={handleEnablePush}
        >
          {pushBusy ? 'Working…' : 'Enable notifications'}
        </Button>
        <Button
          variant="outline-secondary"
          disabled={!canDisable || pushBusy}
          onClick={handleDisablePush}
        >
          Disable
        </Button>
      </div>
      {pushError && <Alert className="mt-3 mb-0" variant="danger">{pushError}</Alert>}
      {pushMessage && <Alert className="mt-3 mb-0" variant="info">{pushMessage}</Alert>}
    </section>
  );
}

function Settings() {
  const { activeUser } = useUser();
  const { status, material, setupEncryption, unlockWithPassword } = useCrypto();
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ensureUnlocked = async () => {
    if (status === 'unlocked' || status === 'none') return;
    if (!password) throw new Error('Enter your login password so this device can create keys.');
    await unlockWithPassword(password);
  };

  const runSetup = async (replaced: boolean) => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      try {
        await ensureUnlocked();
      } catch (err) {
        const wrapPresent = hasPersistedWrapKey();
        if (!wrapPresent || !replaced) throw err;
      }
      await setupEncryption();
      setPassword('');
      setMessage(replaced
        ? 'New chat keys are on this password. Old encrypted threads stay on the previous key until you recover them through Recovery circle.'
        : 'Encryption is on for this device. Use Recovery circle for keyholders.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSetup = async (event) => {
    event.preventDefault();
    await runSetup(false);
  };

  if (!activeUser) {
    return (
      <div className="main-content">
        <Container className="my-4" style={{ maxWidth: 640 }}>
          <p>Log in to manage settings.</p>
          <NotificationsSection loggedIn={false} />
        </Container>
      </div>
    );
  }

  const enabled = status === 'unlocked' || status === 'locked' || material?.enabled;
  const needsPassword = status === 'locked' || (status === 'none' && !hasPersistedWrapKey());
  const hasRetiredShareMetadata = hasConfiguredSssMetadata(material?.sss);

  return (
    <div className="main-content">
      <Container className="my-4" style={{ maxWidth: 640 }}>
        <h1 className="display-6 mb-3">Settings</h1>
        <p>
          When encryption is on, people in the chat can read the messages. The server cannot.
          Keyholders belong on <Link to="/recovery">Recovery circle</Link>.
        </p>
        {hasRetiredShareMetadata && (
          <Alert variant="secondary">
            This account still has old laboratory share metadata. Reusable share files and the
            screening page are retired. Ask keyholders to use Recovery circle instead.
          </Alert>
        )}

        {needsPassword && (
          <Form.Group className="mb-3">
            <Form.Label>Login password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Form.Text>Same password as login. Used only on this device to unlock chat keys.</Form.Text>
          </Form.Group>
        )}

        {!enabled && (
          <Form onSubmit={handleSetup}>
            {error && <Alert variant="danger">{error}</Alert>}
            <Button type="submit" disabled={busy}>
              {busy ? 'Creating keys…' : 'Turn on encryption'}
            </Button>
          </Form>
        )}

        {enabled && (
          <>
            <Alert variant="success">
              Encryption is set up.
              {status === 'unlocked' ? ' Chat keys are unlocked on this device.' : ' Enter your login password to replace keys.'}
            </Alert>
            {error && <Alert variant="danger">{error}</Alert>}
            {status === 'locked' && (
              <Button
                variant="outline-danger"
                disabled={busy}
                onClick={() => runSetup(true)}
              >
                {busy ? 'Creating keys…' : 'Create new chat keys from this password'}
              </Button>
            )}
          </>
        )}

        {message && <Alert className="mt-3" variant="info">{message}</Alert>}

        <NotificationsSection loggedIn />
      </Container>
    </div>
  );
}

export default Settings;
